import React from 'react';
import { AppTable } from '@allygpo/ally-ux';
import { getCoreRowModel, getPaginationRowModel, useReactTable, type ColumnDef, type PaginationState, type Updater } from '@tanstack/react-table';

export interface PayerGroupingRow {
  group: string;
  payers: string[];
  /** DOM id the "jump to group" link scrolls to. */
  rowId: string;
}

/** Master groups per page: the default, then the paginator's choices. */
export const PAYER_GROUPINGS_PER_PAGE = 25;
const ROWS_PER_PAGE_OPTIONS = [10, PAYER_GROUPINGS_PER_PAGE, 50];

const columns: ColumnDef<PayerGroupingRow>[] = [
  {
    id: 'group',
    size: 240,
    header: 'Master payer group',
    cell: ({ row: { original: row } }) => <span id={row.rowId} className="font-medium">{row.group}</span>,
  },
  {
    id: 'payers',
    size: 240,
    header: 'Insurance payer primary',
    cell: ({ row: { original: row } }) => (
      <span className="flex flex-column u-gap-1 text-secondary">{row.payers.map((payer) => <span key={payer}>{payer}</span>)}</span>
    ),
  },
];

interface PayerGroupingsTableProps {
  groups: PayerGroupingRow[];
  /** Page index and size, owned by the page so "jump to group" can turn to the right page. */
  page: number;
  pageSize: number;
  onPagination: (next: PaginationState) => void;
  /** The group a jump landed on. */
  highlight: string | null;
}

/** Master payer groups and the insurance payers in each (Payer eligibility › Payer groupings). */
const PayerGroupingsTable: React.FC<PayerGroupingsTableProps> = ({ groups, page, pageSize, onPagination, highlight }) => {
  const pagination: PaginationState = { pageIndex: page, pageSize };
  const table = useReactTable({
    data: groups,
    columns,
    state: { pagination },
    onPaginationChange: (updater: Updater<PaginationState>) => onPagination(typeof updater === 'function' ? updater(pagination) : updater),
    autoResetPageIndex: false,
    getRowId: (row) => row.group,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });
  return (
    <AppTable
      isBordered={false}
      size="sm"
      ariaLabel="Payer groupings"
      table={table}
      pagination={{ rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS }}
      rowClassName={({ rowData }) => (rowData.group === highlight ? 'bg-surface-selected' : '')}
      messages={{ paginationRangeText: ({ first, last, total }) => `Showing ${first}–${last} of ${total} group${total === 1 ? '' : 's'}` }}
    />
  );
};

export default PayerGroupingsTable;
